export type MergeDataKind = "object" | "array" | "value";

export interface MergeDataNode {
  id: string;
  parentId: string | null;
  key: string;
  label: string;
  kind: MergeDataKind;
  childIds: string[];
  displayValue: string | null;
  path: string;
  templatePath: string;
}

export interface MergeDataIndex {
  rootIds: string[];
  nodes: Record<string, MergeDataNode>;
}

export interface MergeDataSearch {
  query: string;
  matchIds: Set<string>;
  ancestorIds: Set<string>;
}

// Array items take the first of these that holds a non-blank string -- the
// same precedence the old HAML page used for its "(Expand)" links.
const LABEL_KEYS = ["name", "resource_name", "phase", "section"];

type PlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is PlainObject =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isBlankString = (value: unknown): boolean =>
  typeof value === "string" && value.trim() === "";

const isEmptyContainer = (value: unknown): boolean =>
  (Array.isArray(value) && value.length === 0) ||
  (isPlainObject(value) && Object.keys(value).length === 0);

export const formatLeafValue = (value: unknown): string => {
  if (value === null || value === undefined || isBlankString(value))
    return "No value";
  if (isEmptyContainer(value)) return "None";
  return String(value);
};

// The placeholders stand in for an absent value; searching "no value" should
// not light up every blank field.
const searchableValue = (value: unknown): string =>
  value === null ||
  value === undefined ||
  isBlankString(value) ||
  isEmptyContainer(value)
    ? ""
    : String(value);

const itemLabel = (item: unknown, index: number): string => {
  if (isPlainObject(item)) {
    for (const key of LABEL_KEYS) {
      const candidate = item[key];
      if (typeof candidate === "string" && candidate.trim() !== "")
        return candidate;
    }
  }
  return `[${index}]`;
};

// Raw values are kept out of MergeDataNode (it is display data) but search
// needs them; keyed by the nodes object so each index carries its own.
const searchText = new WeakMap<
  Record<string, MergeDataNode>,
  Record<string, unknown>
>();

export const buildMergeDataIndex = (content: unknown): MergeDataIndex => {
  const nodes: Record<string, MergeDataNode> = {};
  const rawValues: Record<string, unknown> = {};

  const visit = (
    value: unknown,
    key: string,
    label: string,
    path: string,
    templatePath: string,
    parentId: string | null
  ): string => {
    const container =
      (Array.isArray(value) || isPlainObject(value)) &&
      !isEmptyContainer(value);
    const node: MergeDataNode = {
      id: path,
      parentId,
      key,
      label,
      kind: container ? (Array.isArray(value) ? "array" : "object") : "value",
      childIds: [],
      displayValue: container ? null : formatLeafValue(value),
      path,
      templatePath,
    };
    nodes[path] = node;
    rawValues[path] = value;

    if (Array.isArray(value) && container) {
      node.childIds = value.map((item, i) =>
        visit(
          item,
          String(i),
          itemLabel(item, i),
          `${path}[${i}]`,
          `${templatePath}[]`,
          path
        )
      );
    } else if (isPlainObject(value) && container) {
      node.childIds = Object.keys(value).map((childKey) =>
        visit(
          value[childKey],
          childKey,
          childKey,
          `${path}.${childKey}`,
          `${templatePath}.${childKey}`,
          path
        )
      );
    }
    return path;
  };

  if (!isPlainObject(content)) return { rootIds: [], nodes: {} };

  const rootIds = Object.keys(content).map((key) =>
    visit(content[key], key, key, key, key, null)
  );
  searchText.set(nodes, rawValues);
  return { rootIds, nodes };
};

export const searchMergeData = (
  index: MergeDataIndex,
  query: string
): MergeDataSearch => {
  const needle = query.trim().toLowerCase();
  const matchIds = new Set<string>();
  const ancestorIds = new Set<string>();
  if (!needle) return { query, matchIds, ancestorIds };

  const raw = searchText.get(index.nodes) ?? {};
  Object.values(index.nodes).forEach((node) => {
    const haystacks = [
      node.key,
      node.label,
      node.kind === "value" ? searchableValue(raw[node.id]) : "",
    ];
    if (haystacks.some((text) => text.toLowerCase().includes(needle))) {
      matchIds.add(node.id);
      let parentId = node.parentId;
      while (parentId !== null && !ancestorIds.has(parentId)) {
        ancestorIds.add(parentId);
        parentId = index.nodes[parentId].parentId;
      }
    }
  });
  return { query, matchIds, ancestorIds };
};

export const expandableIds = (index: MergeDataIndex): string[] =>
  Object.values(index.nodes)
    .filter((node) => node.kind !== "value")
    .map((node) => node.id);
